import _ from "lodash"
import { useMemo } from "react"
import { createBrowserHistory } from "history"
import {
  useTransition,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useState,
  useLayoutEffect,
} from "react"
import {
  readCountries,
  readVisaCountries,
  readStates,
  readVisaStatus,
  readCities,
  readObjectStatuses,
  readAllCurrencies,
  readSupplierServices,
  readPaymentModes,
  readSuppliers,
} from "common-services"

/**
 * Combines multiple class names into a single string.
 *
 * This function takes any number of arguments and concatenates them into a single string,
 * separated by spaces, to be used as class names. It ignores null or undefined values.
 *
 * @param {...(string|null|undefined)} args - The class names to be combined.
 * @returns {string} A single string containing all the valid class names separated by spaces.
 *
 * @example
 * const className = classes('btn', 'btn-primary', null, undefined, 'active');
 * @output "btn btn-primary active"
 */
export const history = createBrowserHistory()

export const classes = (...args) => {
  const result = _.reduce(
    _.compact(args),
    (r, arg) => r.concat(_.trim(arg)),
    [],
  )
  return _.size(result) ? result.join(" ") : null
}

/**
 * Generates CSS variables from a given style object.
 * @param {Object} styleObject - An object containing CSS properties and their values.
 * @returns {string[]} An array of CSS variable strings.
 *
 * @example
 *
 * const styleObject = {
 *   primaryColor: '#ffffff',
 *   secondaryColor: '#000000',
 * };
 *
 * const cssVariables = generateCSSVariables(styleObject);
 * // Output: ["--primaryColor: #ffffff;", "--secondaryColor: #000000;"]
 */
const parseError = (response, data, reject) => {
  if (response.status === 401) {
    sessionStorage.clear()
    history.push("/")
    reject(data)
  }
  if (response.status >= 400) {
    reject({ ...data })
  }
}

export const asyncSuccess = (response) => {
  return new Promise((resolve, reject) => {
    if (response.status === 401) {
      sessionStorage.clear()
      window.location = "/"
    }
    const contentType = response.headers.get("Content-Type")
    if (response.ok) {
      if (contentType.includes("application/json"))
        response.json().then(resolve) // ✅ Handle JSON responses
      else if (contentType.startsWith("text/"))
        response.text().then(resolve) // ✅ Handle plain text responses (txt, csv, html, etc.)
      else response.blob().then(resolve) // ✅ Assume it's a binary file (Blob) if not JSON or text
    } else if (response.status >= 400 && response.status <= 599) {
      response
        .json()
        .then((json) => {
          reject(parseError(response, json, reject))
        })
        .catch((err) => {
          reject(parseError(response, err, reject))
        })
    } else {
      reject(response)
    }
  })
}

export const axiosSuccess = (response) => {
  return new Promise((resolve, reject) => {
    // Handle unauthorized access
    if (response.status === 401) {
      sessionStorage.clear()
      window.location = "/"
      return // Ensure we exit early.
    }

    // If the response status indicates success
    if (response.status >= 200 && response.status < 300) {
      // Axios already parses JSON responses, so use response.data directly.
      resolve(response.data)
    }
    // Handle error status codes
    else if (response.status >= 400 && response.status <= 599) {
      // You can use response.data (or any other relevant properties) to build your error.
      reject(parseError(response, response.data))
    } else {
      reject(response)
    }
  })
}

export const asyncFailure = (error) =>
  Promise.reject(error && { message: error.message })

export const axiosFailure = (error) =>
  Promise.reject(error && { message: error.response?.data?.message })

export const getRESTHeader = () => {
  const userToken = sessionStorage.getItem("userToken")
  if (userToken) {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${userToken}`,
    }
  }
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
  }
}

export const generateCSSVariables = (styleObject) =>
  _.map(_.entries(styleObject), ([key, value]) => `${key}:${value}`)

const abortController = new AbortController()

export const useService = (fn) => {
  const [, startTransition] = useTransition()
  // Can be a ref as we don't need to fire a request at every re-render.
  // For this, the isAsyncPending need to have a new reference everytime.
  const isAsyncPending = useRef(null)
  const [data, setData] = useState()
  const [, forceUpdate] = useReducer((x) => x + 1, 0) // Force re-render

  const onBeginTransition = useCallback(
    (args) => {
      return new Promise((resolve, reject) =>
        startTransition(async () => {
          isAsyncPending.current = true
          try {
            const result = await fn({ ...args })
            setData(result)
            resolve(result)
          } catch ({ errorMessage, errorDetails, message }) {
            setData(null)
            reject(
              errorMessage || errorDetails || message || t("errors.service"),
            )
            isAsyncPending.current = null
          } finally {
            isAsyncPending.current = null
            forceUpdate()
          }
        }),
      )
    },
    [fn],
  )

  useEffect(() => () => {
    abortController.abort()
  })

  return [data, isAsyncPending.current, onBeginTransition]
}

export const useSuppliers = () => {
  const [supplierResource = {}, isReadSuppliersPending, startReadSuppliers] =
    useService(readSuppliers)

  const supplierNames = useMemo(
    () =>
      _.uniqBy(
        _.map(supplierResource.data, ({ supplierName }) => ({
          label: supplierName,
          value: supplierName,
        })),
        "value",
      ),
    [supplierResource.data],
  )

  const supplierTypes = useMemo(
    () =>
      _.uniqBy(
        _.map(supplierResource.data, ({ supplierType }) => ({
          label: supplierType,
          value: supplierType,
        })),
        "value",
      ),
    [supplierResource.data],
  )

  const supplierServices = useMemo(
    () =>
      _.uniqBy(
        _.map(supplierResource.data, ({ supplierService }) => ({
          label: supplierService,
          value: supplierService,
        })),
        "value",
      ),
    [supplierResource.data],
  )

  const supplierSources = useMemo(
    () =>
      _.uniqBy(
        _.map(
          supplierResource.data,
          ({ supplierName, supplierSourceCode }) => ({
            label: supplierName,
            value: supplierSourceCode,
          }),
        ),
        "value",
      ),
    [supplierResource.data],
  )

  const getSupplierName = useCallback(
    (value) => _.find(supplierSources, { value })?.label,
    [supplierSources],
  )

  useLayoutEffect(() => {
    startReadSuppliers({ query: { page: 0, size: 10000 } })
  }, [startReadSuppliers])

  return {
    supplierNames,
    supplierTypes,
    supplierServices,
    supplierSources,
    getSupplierName,
    isReadSuppliersPending,
  }
}

export const agencyTypes = [
  { label: "Parent Agency", value: "PARENT_AGENCY" },
  { label: "Selling Agency", value: "SELLING_AGENCY" },
]

export const getQueryParams = (query = {}) => {
  let queryParams = ""
  Object.keys(query).forEach((key, i) => {
    if (i === 0) queryParams += "?"
    else queryParams += "&"
    queryParams += `${key}=${query[key]}`
  })
  return queryParams
}

export const objectToQueryParams = (params) => {
  const searchParams = new URLSearchParams(params)
  return searchParams ? `?${searchParams.toString()}` : ""
}

export const belowViewPort = (width, styles) => ({
  [`@media only screen and (max-width: ${width}px)`]: styles,
})

export const aboveViewPort = (width, styles) => ({
  [`@media only screen and (min-width: ${width}px)`]: styles,
})

export const formatDate = (date) => {
  if (!date) return ""
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]
  const dateObj = new Date(date)
  const month = monthNames[dateObj.getMonth()]
  const day = String(dateObj.getDate()).padStart(2, "0")
  const year = dateObj.getFullYear()
  return `${day}-${month}-${year}`
}

export const formatDateAndTime = (date) => {
  if (!date) return ""

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]

  const dateObj = new Date(date)
  const month = monthNames[dateObj.getMonth()]
  const day = String(dateObj.getDate()).padStart(2, "0")
  const year = dateObj.getFullYear()

  const hours = String(dateObj.getHours()).padStart(2, "0")
  const minutes = String(dateObj.getMinutes()).padStart(2, "0")

  return `${day}-${_.toUpper(month)}-${year} | ${hours}:${minutes}`
}

export const validateFileSize = (files, size) => {
  if (!files) return true
  if (!_.isArray(files)) {
    if (files.size >= size) return false
    return true
  } else {
    if (_.find(files, ({ size: fileSize }) => fileSize >= size)) return false
    return true
  }
}

export const titles = [
  {
    label: "Mr",
    value: "Mr",
  },
  {
    label: "Ms",
    value: "Ms",
  },
  {
    label: "Mrs",
    value: "Mrs",
  },
]

export const travelTypes = [
  {
    label: "Domestic",
    value: "DOMESTIC",
  },
  {
    label: "International",
    value: "INTERNATIONAL",
  },
  {
    label: "SOTO",
    value: "SOTO",
  },
]

export const dateWithoutTime = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

export const useSlidePanel = (SlidePanel) => {
  const [slidePanelProps, setSlidePanelProps] = useState()

  const showPanel = useCallback((props) => {
    setSlidePanelProps(undefined)
    return new Promise((resolve) =>
      setSlidePanelProps({
        ...props,
        onClose: (value) => {
          setSlidePanelProps(undefined)
          resolve(value)
        },
      }),
    )
  }, [])

  return [showPanel, slidePanelProps && <SlidePanel {...slidePanelProps} />]
}

export const passwordValidationRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/

export const mobileNumberValidationRegex = /^[0-9]{10}$/

export const coiValidationRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{21}$/

export const zipCodeRegex = /^[0-9]{6}$/

export const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const aadharValidationRegex = /^[0-9]{12}$/

export const panCardValidationRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{10}$/

export const nameRegex = /^[A-Za-z][A-Za-z-' ]{1,49}$/

export const gstRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{15}$/

export const useCountries = () => {
  const [countriesResource = {}, isReadCountriesPending, startReadCountries] =
    useService(readCountries)

  const countries = useMemo(
    () =>
      _.map(countriesResource.data, (country) => ({
        label: country.countryName,
        value: country.countryCode,
      })),
    [countriesResource?.data],
  )

  const isdCodes = useMemo(
    () =>
      _.map(countriesResource.data, (country) => ({
        label: `+${country.isdCode}`,
        value: country.isdCode,
      })),
    [countriesResource?.data],
  )

  useEffect(() => {
    startReadCountries()
  }, [startReadCountries])

  return { countries, isdCodes, isReadCountriesPending }
}

export const useVisaCountries = () => {
  const [countriesResource = {}, isReadCountriesPending, startReadCountries] =
    useService(readVisaCountries)

  const countries = useMemo(
    () =>
      _.map(countriesResource.data, (country) => ({
        label: country.countryName,
        value: country.countryCode,
      })),
    [countriesResource.data],
  )

  useEffect(() => {
    startReadCountries()
  }, [startReadCountries])

  return { countries, isReadCountriesPending }
}

export const useVisaStatus = () => {
  const [statuses = {}, isVisaStatusesPending, startReadVisaStatuses] =
    useService(readVisaStatus)

  const visaStatuses = useMemo(
    () =>
      _.map(statuses.data, (status) => ({
        label: status.statusName,
        value: status.id,
      })),
    [statuses.data],
  )

  const findVisaStatus = useCallback(
    (l) =>
      _.find(visaStatuses, ({ label }) => _.toLower(label) === _.toLower(l))
        ?.value ?? {},
    [visaStatuses],
  )

  useEffect(() => {
    startReadVisaStatuses()
  }, [startReadVisaStatuses])

  return { visaStatuses, isVisaStatusesPending, findVisaStatus }
}

export const useCountryStates = () => {
  const [statesResource = {}, isReadStatesPending, startReadStates] =
    useService(readStates)

  const states = useMemo(
    () =>
      _.map(statesResource.data, ({ stateCode, stateName }) => ({
        label: stateName,
        value: stateCode,
      })),
    [statesResource?.data],
  )

  const fetchStates = useCallback(
    (payload) => startReadStates(payload),
    [startReadStates],
  )

  useEffect(() => {
    // TODO: Remove after BE starts supporting country based search
    startReadStates()
  }, [startReadStates])

  return { states, isReadStatesPending, fetchStates }
}

export const useCities = ({ stateCode, countryCode } = {}) => {
  const [citiesResource = {}, isReadCitiesPending, startReadCities] =
    useService(readCities)

  const cities = useMemo(
    () =>
      _.map(citiesResource.data, ({ cityCode, cityName }) => ({
        label: cityName,
        value: cityCode,
      })),
    [citiesResource.data],
  )

  const fetchCities = useCallback(
    (payload) => startReadCities(payload),
    [startReadCities],
  )

  useEffect(() => {
    if (stateCode || countryCode)
      startReadCities(
        _.omitBy(
          { stateCode, countryCode },
          (value) => value === null || value === undefined,
        ),
      )
  }, [countryCode, startReadCities, stateCode])

  return { cities, isReadCitiesPending, fetchCities }
}

export const supplierMappedStatuses = [
  { label: "Yes", value: "YES" },
  { label: "No", value: "NO" },
]

export const toCurrencyFormat = (value) => {
  const num = Number(value) || 0
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const balanceTypes = [
  { label: "Credit", value: "CREDIT" },
  { label: "Debit", value: "DEBIT" },
]

export const depositTypes = [
  { label: "Online", value: "online" },
  { label: "Offline", value: "offline" },
]

export const useCurrencies = () => {
  const [currenciesResource = {}, isCurrenciesPending, startReadCurrencies] =
    useService(readAllCurrencies)

  const currencies = useMemo(
    () =>
      _.map(currenciesResource.data, (currency) => ({
        label: currency.currencyCode,
        value: currency.currencyCode,
      })),
    [currenciesResource.data],
  )

  useEffect(() => {
    startReadCurrencies()
  }, [startReadCurrencies])

  return {
    currencies,
    isCurrenciesPending,
    startReadCurrencies,
  }
}

export const useSupplierServices = () => {
  const [
    supplierServicesResource = {},
    isSupplierServicesPending,
    startReadSupplierServices,
  ] = useService(readSupplierServices)

  const supplierServices = useMemo(
    () =>
      _.map(supplierServicesResource.data, ({ serviceName, id }) => ({
        label: _.startCase(_.toLower(serviceName)),
        value: id,
      })),
    [supplierServicesResource.data],
  )

  const getSupplierServiceId = useCallback(
    (l) =>
      _.find(supplierServices, ({ label }) => _.toLower(label) === l)?.value,
    [supplierServices],
  )

  useEffect(() => {
    startReadSupplierServices()
  }, [startReadSupplierServices])

  return {
    supplierServices,
    isSupplierServicesPending,
    startReadSupplierServices,
    getSupplierServiceId,
  }
}

export const useObjectStatuses = () => {
  const [
    objectStatusResource = {},
    isReadObjectStatusesPending,
    startReadObjectStatuses,
  ] = useService(readObjectStatuses)

  const objectStatuses = useMemo(
    () =>
      _.map(objectStatusResource.data, (status) => ({
        label: _.startCase(_.toLower(status.description)),
        value: status.id,
      })),
    [objectStatusResource],
  )

  const getObjectStatusValue = useCallback(
    (l) =>
      _.find(objectStatuses, ({ label }) => _.toLower(label) === _.toLower(l))
        ?.value,
    [objectStatuses],
  )

  const filterObjectStatuses = useCallback(
    (statuses) =>
      _.filter(objectStatuses, (status) =>
        _.some(statuses, (s) => _.toLower(s).includes(_.toLower(status.label))),
      ),
    [objectStatuses],
  )

  useEffect(() => {
    startReadObjectStatuses()
  }, [startReadObjectStatuses])

  return {
    objectStatuses,
    filterObjectStatuses,
    isReadObjectStatusesPending,
    getObjectStatusValue,
  }
}

export const flattenObject = (obj, prefix = "") =>
  Object.keys(obj).reduce((acc, key) => {
    const value = obj[key]
    const newKey = prefix ? `${prefix}.${key}` : key

    if (value instanceof File || value instanceof Blob) {
      // Directly add File or Blob objects without flattening
      acc[newKey] = value
    } else if (Array.isArray(value)) {
      // Handle arrays, including arrays of files
      value.forEach((item, index) => {
        if (item instanceof File || item instanceof Blob) {
          // Preserve file in the array
          acc[`${newKey}[${index}]`] = item
        } else if (_.isObject(item)) {
          // Flatten objects within the array
          Object.assign(acc, flattenObject(item, `${newKey}[${index}]`))
        } else {
          // Add primitive values from the array
          acc[`${newKey}[${index}]`] = item
        }
      })
    } else if (_.isObject(value) && !Array.isArray(value)) {
      // Recursively flatten nested objects
      Object.assign(acc, flattenObject(value, newKey))
    } else {
      // Add other primitive values directly
      acc[newKey] = value
    }

    return acc
  }, {})

export const featuresFlatMap = (features) => {
  const groupedFeatures = _.groupBy(features, (item) =>
    item.label.toLowerCase(),
  )

  // Transform each group
  const result = _.mapValues(groupedFeatures, (group) => {
    return _.reduce(
      group,
      (acc, item) => {
        const featureKey = _.kebabCase(item.feature) // Convert feature to kebab-case
        acc[featureKey] = item.id // Map feature to ID
        return acc
      },
      {},
    )
  })

  return result
}

export const usePaymentModes = (query) => {
  const [
    paymentModesResource = {},
    isReadPaymentModesPending,
    startReadPaymentModes,
  ] = useService(readPaymentModes)

  const paymentModes = useMemo(
    () =>
      _.map(paymentModesResource.data, ({ id, name }) => ({
        label: name,
        value: id,
      })),
    [paymentModesResource.data],
  )

  const getPaymentMode = useCallback(
    (l) => _.find(paymentModes, ({ label }) => _.toLower(label) === l)?.value,
    [paymentModes],
  )

  const filterPaymentModes = useCallback(
    (modes) =>
      _.filter(paymentModes, (mode) =>
        _.includes(modes, _.toLower(mode.label)),
      ),
    [paymentModes],
  )

  useEffect(() => {
    startReadPaymentModes({ query })
  }, [query, startReadPaymentModes])

  return {
    paymentModes,
    isReadPaymentModesPending,
    filterPaymentModes,
    getPaymentMode,
  }
}

export const getToday = () => new Date().setHours(0, 0, 0, 0)

export const getDaysBefore = (days) => Date.now() - days * 24 * 60 * 60 * 1000

export const visaCountryAllowedOptions = [
  { label: "All countries allowed", value: "all" },
  { label: "Non UAE countries allowed", value: "non-uae" },
]

export const numberToWords = (num) => {
  if (num === 0) return "Zero"

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ]

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ]

  const scales = ["", "Thousand", "Million", "Billion", "Trillion"]

  const convertHundreds = (n) => {
    let result = ""

    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred"
      n %= 100
      if (n > 0) result += " "
    }

    if (n >= 20) {
      result += tens[Math.floor(n / 10)]
      n %= 10
      if (n > 0) result += " "
    }

    if (n > 0) {
      result += ones[n]
    }

    return result
  }

  // Handle decimal part
  const parts = num.toString().split(".")
  const integerPart = parseInt(parts[0])

  // Process integer part
  let result = ""
  if (integerPart === 0) {
    result = "Zero"
  } else {
    const chunks = []
    let tempNum = integerPart

    while (tempNum > 0) {
      chunks.push(tempNum % 1000)
      tempNum = Math.floor(tempNum / 1000)
    }

    for (let i = chunks.length - 1; i >= 0; i--) {
      if (chunks[i] !== 0) {
        const chunk = convertHundreds(chunks[i])
        if (i > 0) {
          result += chunk + " " + scales[i] + " "
        } else {
          result += chunk
        }
      }
    }
    result = result.trim()
  }

  // Process decimal part if it exists
  if (parts.length > 1 && parts[1] !== "0") {
    const decimalPart = parts[1]
    result += " Point"

    // Convert each digit of the decimal part
    for (let i = 0; i < decimalPart.length; i++) {
      const digit = parseInt(decimalPart[i])
      result += " " + ones[digit]
    }
  }

  // Add "Only" at the end
  result = result.trim() + " Only"

  return result
}

// Function specifically for currency (removes decimal part)
export const numberToWordsForCurrency = (amount) => {
  const numericAmount = parseFloat(amount.toString().replace(/,/g, ""))
  const integerPart = Math.floor(numericAmount)

  return numberToWords(integerPart)
}

// Alternative currency function that includes cents/paise
export const numberToWordsForCurrencyWithCents = (
  amount,
  currencyName = "Rupees",
  centName = "Paise",
) => {
  const numericAmount = parseFloat(amount.toString().replace(/,/g, ""))
  const integerPart = Math.floor(numericAmount)
  const decimalPart = Math.round((numericAmount - integerPart) * 100)

  let result = ""

  if (integerPart > 0) {
    const integerWords = numberToWords(integerPart).replace(" Only", "")
    result += integerWords + " " + currencyName
  }

  if (decimalPart > 0) {
    if (integerPart > 0) result += " and "
    const decimalWords = numberToWords(decimalPart).replace(" Only", "")
    result += decimalWords + " " + centName
  }

  if (integerPart === 0 && decimalPart === 0) {
    result = "Zero " + currencyName
  }

  return result + " Only"
}

// Helper function to get a specific cookie
export const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

export const clearCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}
